import { useLocation, useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  LoginResponseData,
  UserSettingsApiUserSettingsControllerVerifyDeleteAccountRequest,
} from '@EHSNavigator/backend';
import { useState, useRef, useEffect } from 'react';
import { useApiClients } from '../../client/api/useApiClient';
import Loader from '../../components/Loader';
import BlueLinkIcon from '../../assets/icons/blue-link-icon.svg?react';

import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import InfoContent from '../../components/InfoContent';
import clsx from 'clsx';
import { useLocalStorage } from '@uidotdev/usehooks';
import { logout } from '../../actions/useAction';
import { useDispatch } from 'react-redux';

export default function DeleteAccountPage() {
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const userId = query.get('userId');
  const token = query.get('token');
  const dispatch = useDispatch();
  const deleteAccountAttempted = useRef(false);
  const [, setUserInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );

  const { UserSettingsApi } = useApiClients();

  const deleteAccountMutation = useMutation<
    any,
    Error,
    UserSettingsApiUserSettingsControllerVerifyDeleteAccountRequest['verifyDeleteAccountRequestDTO']
  >({
    mutationFn: async (
      data: UserSettingsApiUserSettingsControllerVerifyDeleteAccountRequest['verifyDeleteAccountRequestDTO'],
    ) => {
      const response =
        await UserSettingsApi.userSettingsControllerVerifyDeleteAccount({
          verifyDeleteAccountRequestDTO: {
            ...data,
          },
        });
      return response.data;
    },

    onSuccess: (data) => {
      setIsLoading(false);
      toast.success(data?.message);
       setTimeout(async() => {
        await dispatch(logout(setUserInfo) as any);        
      }, 10000);
    },
    onError: (error: any) => {
      setIsLoading(false);
      const errorMessage = error?.response?.data?.message;
      if (errorMessage.includes('expired')) {
        setIsExpired(true);
      }
    },
  });

  useEffect(() => {
    if (userId && token && !deleteAccountAttempted.current) {
      deleteAccountAttempted.current = true;
      deleteAccountMutation.mutate({
        token: String(token),
        userId: String(userId),
      });
    }
  }, [userId, token, deleteAccountMutation]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <main className="relative z-10 flex flex-1 flex-col items-center justify-between gap-10 pb-5">
      <div className="flex flex-1 flex-col justify-center">
        {isExpired ? (
          <div className="mx-auto flex max-w-685 flex-col items-center gap-12 px-25">
            <BlueLinkIcon className="[&>path]:fill-brand-byzantine h-100 w-100 lg:h-150 lg:w-150" />
            <div className="flex flex-col gap-2.5 text-center">
              <h2 className={clsx('text-neutral-tones-text font-bold')}>
                Link Expired
              </h2>
              <p className="font-inter-variable text-brand-byzantine text-2xl font-medium italic">
                you can request a new link by using the settings.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col justify-center">
            <InfoContent
              icon={<WarningIcon className="h-100 w-100 lg:h-150 lg:w-150" />}
              title="Account Deleted Successfully"
              description="Your account deletion request has been processed successfully. Thank you for using EHS Navigator."
              buttonLabel="Back to Login"
              warn
              handleClick={() => navigate('/')}
            />
          </div>
        )}
      </div>
    </main>
  );
}
